/**
 * Procurement & Inventory module — statuses, attention states, access helpers.
 * Secondary roles participate via resolveUserCapabilities / hasRoleCapability.
 */

import type { UserRole } from "./userRoles";
import {
  hasRoleCapability,
  resolveUserRoleSet,
  type UserRoleFields,
} from "./userRoles";

export const PICK_LIST_LINE_STATUSES = [
  "unreviewed",
  "to_order",
  "ordered",
  "use_existing_stock",
  "on_hold",
  "not_required",
] as const;

export type PickListLineStatus = (typeof PICK_LIST_LINE_STATUSES)[number];

export const PICK_LIST_LINE_STATUS_LABELS: Record<PickListLineStatus, string> =
  {
    unreviewed: "Unreviewed",
    to_order: "To Order",
    ordered: "Ordered",
    use_existing_stock: "Use Existing Stock",
    on_hold: "On Hold",
    not_required: "No Order Required",
  };

export const NO_ORDER_REQUIRED_ACTION_LABEL = "MARK AS NO ORDER REQUIRED";
export const REOPEN_FOR_ORDERING_ACTION_LABEL = "REOPEN FOR ORDERING";

/**
 * Statuses `updateLine` may set directly. `not_required` is deliberately excluded —
 * only `markNoOrderRequired` may set it (reason + immutable audit event required),
 * and only `reopenForOrdering` may clear it (note required, restores prior status).
 */
export const PICK_LIST_LINE_STATUSES_UPDATABLE = PICK_LIST_LINE_STATUSES.filter(
  (status): status is Exclude<PickListLineStatus, "not_required"> =>
    status !== "not_required"
);

export const PROJECT_PROCUREMENT_STATUSES = [
  "active",
  "payment_reversed_hold",
  "deactivated",
  "completed",
] as const;

export type ProjectProcurementStatus =
  (typeof PROJECT_PROCUREMENT_STATUSES)[number];

export const PROJECT_PROCUREMENT_STATUS_LABELS: Record<
  ProjectProcurementStatus,
  string
> = {
  active: "Active",
  payment_reversed_hold: "Payment Reversed — Procurement Hold",
  deactivated: "Deactivated",
  completed: "Closed",
};

/** User-facing closure kind labels (DB enum completionKind). */
export const PROCUREMENT_COMPLETION_KIND_LABELS = {
  normal: "Closed",
  previously_completed: "Closed — historical work completed before Portal",
} as const;

export type ProcurementCompletionKind =
  keyof typeof PROCUREMENT_COMPLETION_KIND_LABELS;

/** Statuses that still appear on the live procurement dashboard. */
export const OPEN_PROJECT_PROCUREMENT_STATUSES: readonly ProjectProcurementStatus[] =
  ["active", "payment_reversed_hold"] as const;

export function isProcurementCompleted(
  status: string | null | undefined
): boolean {
  return status === "completed";
}

export const PROCUREMENT_ATTENTION_STATES = [
  "new_review",
  "payment_reversed",
  "vo_change",
  "item_hold",
  "ordered_qty_vs_vo",
  "none",
] as const;

export type ProcurementAttentionState =
  (typeof PROCUREMENT_ATTENTION_STATES)[number];

export const PROCUREMENT_ATTENTION_LABELS: Record<
  ProcurementAttentionState,
  string
> = {
  new_review: "New project — review proposal items",
  payment_reversed: "Payment reversed",
  vo_change: "VO change — review",
  item_hold: "Item on hold",
  ordered_qty_vs_vo: "Ordered qty exceeds VO scope",
  none: "",
};

/**
 * `new_review` is an activation prompt, not a permanent business status.
 * Once staff have reviewed the case or any pick-list line has moved beyond its
 * initial unreviewed state, presenting the activation prompt is stale and
 * misleading. Other attention states remain authoritative until their own
 * workflows clear them.
 */
export function resolvePresentedProcurementAttentionState(input: {
  attentionState: ProcurementAttentionState | string | null | undefined;
  reviewedAt?: Date | string | null;
  hasProgressedLine?: boolean;
}): ProcurementAttentionState {
  const state = PROCUREMENT_ATTENTION_STATES.includes(
    input.attentionState as ProcurementAttentionState,
  )
    ? (input.attentionState as ProcurementAttentionState)
    : "none";
  if (
    state === "new_review" &&
    (Boolean(input.reviewedAt) || Boolean(input.hasProgressedLine))
  ) {
    return "none";
  }
  return state;
}

/** Roles that may view Procurement (dashboard + pick list read). */
export const PROCUREMENT_MODULE_READ_ROLES: readonly UserRole[] = [
  "admin",
  "finance",
  "inventory",
] as const;

/** Roles that may manage pick lists / mark ordered. */
export const PROCUREMENT_MODULE_MUTATE_ROLES: readonly UserRole[] = [
  "admin",
  "inventory",
] as const;

/** Users who receive Inventory Team notification emails. */
export const INVENTORY_TEAM_NOTIFY_ROLES: readonly UserRole[] = [
  "inventory",
] as const;

export function canAccessProcurementModule(
  user: UserRoleFields | null | undefined
): boolean {
  return hasRoleCapability(user, PROCUREMENT_MODULE_READ_ROLES);
}

export function canManageProcurement(
  user: UserRoleFields | null | undefined
): boolean {
  return hasRoleCapability(user, PROCUREMENT_MODULE_MUTATE_ROLES);
}

export function canOverrideOrderedQuantity(
  user: UserRoleFields | null | undefined
): boolean {
  return hasRoleCapability(user, ["admin"]);
}

export function canViewProcurementLandedCostHistory(
  user: UserRoleFields | null | undefined
): boolean {
  return hasRoleCapability(user, ["admin", "finance"]);
}

export function isInventoryTeamMember(
  user: UserRoleFields | null | undefined
): boolean {
  return hasRoleCapability(user, INVENTORY_TEAM_NOTIFY_ROLES);
}

/**
 * Preserve existing Sales access: all staff except marketing-only.
 * Inventory-only users may still open Sales if they choose; Procurement is separate.
 */
export function canAccessSalesModule(
  user: UserRoleFields | null | undefined
): boolean {
  if (!user) return false;
  const roles = resolveUserRoleSet(user);
  if (roles.size === 0) return false;
  if (roles.has("marketing") && !Array.from(roles).some(r => r !== "marketing"))
    return false;
  return true;
}

/** Preserve existing Service access: all staff except marketing-only. */
export function canAccessServiceModule(
  user: UserRoleFields | null | undefined
): boolean {
  return canAccessSalesModule(user);
}

export function portalModuleAccessForUser(
  user: UserRoleFields | null | undefined
): {
  sales: boolean;
  service: boolean;
  procurement: boolean;
} {
  return {
    sales: canAccessSalesModule(user),
    service: canAccessServiceModule(user),
    procurement: canAccessProcurementModule(user),
  };
}

export function formatPercentOrderedDisplay(input: {
  percentOrdered: number;
  orderedValue: number;
  totalEquipmentValue: number;
}): string {
  const pct = Number.isFinite(input.percentOrdered) ? input.percentOrdered : 0;
  return formatPct(pct);
}

/** Compact dashboard secondary line — ordered value only (equipment total is its own column). */
export function formatPercentOrderedSecondary(input: {
  orderedValue: number;
}): string {
  return `${formatSgd(input.orderedValue)} ordered`;
}

function formatPct(n: number): string {
  if (Number.isInteger(n)) return `${n}%`;
  return `${n.toFixed(1).replace(/\.0$/, "")}%`;
}

function formatSgd(n: number): string {
  const v = Number.isFinite(n) ? n : 0;
  return `$${v.toLocaleString("en-SG", { minimumFractionDigits: 0, maximumFractionDigits: 2 })}`;
}

/** True when any pick-list activity beyond initial snapshot exists. */
export function hasProcurementActivityBeyondActivation(input: {
  reviewedAt?: Date | string | null;
  lineStatusChanges?: number;
  anyLineNotUnreviewed?: boolean;
}): boolean {
  if (input.reviewedAt) return true;
  if ((input.lineStatusChanges ?? 0) > 0) return true;
  if (input.anyLineNotUnreviewed) return true;
  return false;
}
